import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { DocumentType, TagReadDTO } from '../../api/dto';
import { DocumentHeader } from '../../components/documents/DocumentHeader';
import { getReadChangeRequestActivityData, isCreateVariant } from '../../forms/utils';
import { ActivityIcon } from '../../lib/information/activityNim';
import { getActivityChangeRequestPreviewStatus, getActivityStatus } from '../../lib/status';
import { getRoute, ROUTES } from '../../routes/routes';
import { ActivityPageData } from './ActivityPageData';
import { ActivityPageVariant } from './ActivityPageVariant';

export type ExtendedActivityPageData = ActivityPageData &
    (
        | (ActivityPageData & { variant: ActivityPageVariant.READ; changeRequest: boolean })
        | { variant: Exclude<ActivityPageVariant, ActivityPageVariant.READ> }
    );

interface ActivityContentHeaderProps {
    data: ExtendedActivityPageData;
    mask?: string;
    editable: boolean;
    isLoading: boolean;
    onRefreshClick?: () => void;
    Main?: React.ReactNode;
    Left?: React.ReactNode;
    Right?: React.ReactNode;
    showTags?: boolean;
    tags?: TagReadDTO[];
}

export const ActivityContentHeader: React.FC<ActivityContentHeaderProps> = ({ data, ...rest }) => {
    const intl = useIntl();
    const { variant } = data;
    const navigate = useNavigate();

    const getVersions = () => {
        if (variant !== ActivityPageVariant.READ && variant !== ActivityPageVariant.READ_CHANGE_REQUEST) {
            throw new Error();
        }
        return [
            variant === ActivityPageVariant.READ
                ? {
                      versionNumber: data.activityData.id,
                      status: getActivityStatus(data.activityData, intl),
                  }
                : {
                      versionNumber: data.activityData.changeRequestActivity!.id,
                      status: getActivityStatus(data.activityData.changeRequestActivity! as any, intl),
                  },
            ...data.activityData.changeRequests.map((changeRequest) => ({
                versionNumber: changeRequest.id,
                status: getActivityChangeRequestPreviewStatus(changeRequest, intl),
            })),
        ];
    };

    return (
        <DocumentHeader
            documentType={DocumentType.ACTIVITY}
            documentId={
                (variant === ActivityPageVariant.READ && !data.changeRequest) ||
                variant === ActivityPageVariant.READ_CHANGE_REQUEST
                    ? data.activityData.id
                    : null
            }
            icon={<ActivityIcon />}
            title={
                variant === ActivityPageVariant.READ
                    ? intl.formatMessage(
                          {
                              id: !data.changeRequest
                                  ? data.activityData.isTemplate
                                      ? 'activity.templateTitleExisting'
                                      : 'activity.titleExisting'
                                  : 'activity.changeRequest.titleNew',
                          },
                          { id: data.activityData.id }
                      )
                    : isCreateVariant(variant)
                    ? variant === ActivityPageVariant.CREATE_TEMPLATE
                        ? intl.formatMessage({ id: 'activity.titleNewTemplate' })
                        : intl.formatMessage({ id: 'activity.titleNew' })
                    : variant === ActivityPageVariant.CLONE
                    ? data.activityData.isTemplate
                        ? intl.formatMessage(
                              { id: 'activity.titleNewCloningATemplate' },
                              { activityId: data.activityData.id }
                          )
                        : intl.formatMessage({ id: 'activity.titleNewCloning' }, { activityId: data.activityData.id })
                    : variant === ActivityPageVariant.CLONE_FROM_TEMPLATE
                    ? intl.formatMessage(
                          { id: 'activity.titleNewCloningFromTemplate' },
                          { activityId: data.activityData.id }
                      )
                    : intl.formatMessage(
                          { id: 'activity.changeRequest.titleExisting' },
                          {
                              id: getReadChangeRequestActivityData(data).activityData.changeRequestActivity!.id,
                              changeRequestId: getReadChangeRequestActivityData(data).activityData.id,
                          }
                      )
            }
            status={
                variant === ActivityPageVariant.READ
                    ? !data.changeRequest
                        ? getActivityStatus(data.activityData, intl)
                        : undefined
                    : variant === ActivityPageVariant.READ_CHANGE_REQUEST
                    ? getActivityChangeRequestPreviewStatus({ status: data.activityData.changeRequestStatus! }, intl)
                    : undefined
            }
            creation={
                (variant === ActivityPageVariant.READ && !data.changeRequest) ||
                variant === ActivityPageVariant.READ_CHANGE_REQUEST
                    ? data.activityData
                    : undefined
            }
            version={
                // (null-safety for robustness)
                (variant === ActivityPageVariant.READ || variant === ActivityPageVariant.READ_CHANGE_REQUEST) &&
                data.activityData.changeRequests.length > 0
                    ? {
                          currentVersionNumber: data.activityData.id,
                          versions: getVersions(),
                          onSelect: (selectedId) => {
                              // Depending on whether the button was clicked from the context of an Activity of a change request, we need a branch in order to obtain the Activity ID
                              const activityId = data.activityData.changeRequestActivity?.id ?? data.activityData.id;
                              const changeRequest = data.activityData.changeRequests.find((cr) => cr.id === selectedId);
                              return navigate(
                                  getRoute(
                                      changeRequest === undefined
                                          ? {
                                                path: ROUTES.activity.view,
                                                params: {
                                                    id: selectedId,
                                                },
                                            }
                                          : {
                                                path: ROUTES.activity.changeRequest.view,
                                                params: {
                                                    id: activityId,
                                                    changeRequestId: selectedId,
                                                },
                                            }
                                  )
                              );
                          },
                          currentVersionFormatter: () => intl.formatMessage({ id: 'activity.changeRequest.list' }),
                          versionFormatter: (id) => {
                              if (
                                  variant !== ActivityPageVariant.READ &&
                                  variant !== ActivityPageVariant.READ_CHANGE_REQUEST
                              ) {
                                  throw new Error();
                              }
                              const changeRequest = data.activityData.changeRequests.find((cr) => cr.id === id);
                              return changeRequest === undefined
                                  ? intl.formatMessage({ id: 'activity.name' })
                                  : intl.formatMessage(
                                        { id: 'activity.changeRequest.titleExistingShort' },
                                        { changeRequestId: changeRequest.id }
                                    );
                          },
                          noWarning: true,
                      }
                    : undefined
            }
            {...rest}
        />
    );
};
